// P2-B (2026-09-08, spec ADDENDUM 2026-09-08 (b)): Birthdays dashboard widget.
// Condo dashboard widget "🎂 Cumpleaños de este mes". Data source: the SAME
// narrow internal endpoint GET /api/condos/:id/neighbors/birthdays
// (condosAPI.neighborsBirthdays) — directory flag-independent; backend gates to
// directory_visible members with a date_of_birth. Directory endpoint itself is
// disabled server-side per Jaime ruling 2026-09-08 (c) Ruling 1.
// Items carry birthMonthDay ('MM-DD') or null. We filter client-side to the
// current month, sort by day, and render "DD de <mes>". NEVER show year/age.
// Empty state: the widget hides itself entirely (no birthdays this month).
import React, { useEffect, useState } from 'react';
import { Cake } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { condosAPI } from '../services/api';
import { Card } from './ui/Card';

interface NeighborBirthday {
  id: string;
  name: string;
  avatarUrl?: string | null;
  unitLabel?: string | null;
  birthMonthDay?: string | null;
}

interface BirthdayRow {
  id: string;
  name: string;
  avatarUrl?: string | null;
  unitLabel?: string | null;
  day: number;
}

// Tolerant list extraction (same pattern as VecinosPage / Announcements).
function extractNeighbors(raw: any): NeighborBirthday[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  for (const key of ['data', 'neighbors', 'items', 'results']) {
    if (raw[key] !== undefined) {
      const found = extractNeighbors(raw[key]);
      if (found.length > 0) return found;
    }
  }
  return [];
}

const MONTH_NAMES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

function initialOf(name: string): string {
  const trimmed = (name || '').trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
}

export default function BirthdaysWidget() {
  const { user } = useAuth();
  const condoId = user?.condoId;
  const [birthdays, setBirthdays] = useState<BirthdayRow[]>([]);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!condoId) {
      setSettled(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await condosAPI.neighborsBirthdays(condoId);
        if (cancelled) return;
        const now = new Date();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const rows: BirthdayRow[] = extractNeighbors(res?.data)
          .filter((n) => typeof n.birthMonthDay === 'string' && n.birthMonthDay.startsWith(`${month}-`))
          .map((n) => ({
            id: n.id,
            name: n.name,
            avatarUrl: n.avatarUrl,
            unitLabel: n.unitLabel,
            day: parseInt(n.birthMonthDay!.slice(3, 5), 10) || 0,
          }))
          .sort((a, b) => a.day - b.day);
        setBirthdays(rows);
      } catch {
        // Silent: birthdays are a nice-to-have; hide the widget on error.
        setBirthdays([]);
      } finally {
        if (!cancelled) setSettled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [condoId]);

  // Empty state: hide the widget entirely (also while loading, to avoid layout flash).
  if (!settled || birthdays.length === 0) return null;

  const monthName = MONTH_NAMES[new Date().getMonth()];

  return (
    <div className="sm:col-span-2 lg:col-span-3 xl:col-span-4" data-widget="cumpleanos-mes">
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-[var(--v-radius-lg)] bg-[var(--v-teal-50)] text-[var(--v-teal-700)]">
            <Cake className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-bold text-lg text-[var(--v-text-primary)] leading-tight">
              🎂 Cumpleaños de este mes
            </h3>
            <p className="text-sm text-[var(--v-text-secondary)]">{monthName}</p>
          </div>
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {birthdays.map((b) => (
            <li key={b.id} className="flex items-center gap-3">
              {b.avatarUrl ? (
                <img
                  src={b.avatarUrl}
                  alt={b.name}
                  className="w-10 h-10 rounded-full object-cover border border-[var(--v-border-light)]"
                />
              ) : (
                <span className="w-10 h-10 rounded-full bg-[var(--v-teal-50)] text-[var(--v-teal-700)] flex items-center justify-center font-bold">
                  {initialOf(b.name)}
                </span>
              )}
              <div className="min-w-0">
                <p className="font-semibold text-[var(--v-text-primary)] truncate">{b.name}</p>
                <p className="text-sm text-[var(--v-text-secondary)] truncate">
                  {b.unitLabel ? `${b.unitLabel} · ` : ''}
                  {b.day} de {monthName}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
